import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Upload,
  message,
} from "antd";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import DatePicker from "../../components/inputs/MomentDatePicker";
import { uploadToS3 } from "../../services/upload.service";
import { createGroupExpense } from "../../services/groupConsole.service";
import reportCreateResult from "./reportCreateResult";
import useTargetSchools from "./useTargetSchools";

const { Option } = Select;

// Same list ExpensesMain.js (the branch admin's own Expenses screen) offers,
// so a group admin sees identical choices.
const CATEGORIES = [
  "Supplies",
  "Maintenance",
  "Utility",
  "Events",
  "Food & Beverage",
  "Salary",
  "Other",
];

export default function CreateGroupExpenseModal({ groupId, open, onClose }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { targetSchools, selectProps } = useTargetSchools();
  const [uploading, setUploading] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const close = () => {
    form.resetFields();
    setReceipt(null);
    onClose();
  };

  const createMutation = useMutation(
    (payload) => createGroupExpense(groupId, payload),
    {
      onSuccess: (result) => {
        reportCreateResult(result, "Expense");
        queryClient.invalidateQueries("group-console-expenses");
        close();
      },
      onError: (err) => {
        message.error(
          err?.response?.data?.error?.errors?.[0] || "Failed to create expense",
        );
      },
    },
  );

  const handleUploadReceipt = async ({ file, onSuccess, onError }) => {
    try {
      setUploading(true);
      const res = await uploadToS3(file, "expenses");
      setReceipt({ id: res.id, name: res.files.name, url: res.files.original });
      onSuccess(null, file);
    } catch (err) {
      message.error("Failed to upload receipt file.");
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const onFinish = (values) => {
    createMutation.mutate({
      name: values.name,
      amount: values.amount,
      category: values.category === "custom" ? values.customCategory : values.category,
      description: values.description,
      date: values.date ? values.date.toDate() : new Date(),
      receipt: receipt || undefined,
      targetSchools,
    });
  };

  return (
    <Modal
      title="Create Expense"
      open={open}
      onCancel={close}
      footer={null}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item label="Post to" required>
          <Select style={{ width: "100%" }} {...selectProps} />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input placeholder="e.g. Whiteboard markers" />
        </Form.Item>

        <div className="flex gap-4">
          <Form.Item
            name="amount"
            label="Amount"
            className="flex-1"
            rules={[{ required: true, message: "Please enter an amount" }]}
          >
            <InputNumber className="w-full" min={0} placeholder="e.g. 2500" />
          </Form.Item>
          <Form.Item
            name="date"
            label="Expense Date"
            className="flex-1"
            rules={[{ required: true, message: "Please select the date" }]}
          >
            <DatePicker className="w-full" format="DD-MM-YYYY" />
          </Form.Item>
        </div>

        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select>
            {CATEGORIES.map((cat) => (
              <Option key={cat} value={cat}>
                {cat}
              </Option>
            ))}
            <Option value="custom">Custom Category</Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.category !== cur.category}
        >
          {({ getFieldValue }) =>
            getFieldValue("category") === "custom" ? (
              <Form.Item
                name="customCategory"
                label="Custom Category Name"
                rules={[{ required: true, message: "Please enter a category name" }]}
              >
                <Input placeholder="e.g. Sports Day Equipment" />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        <Form.Item name="description" label="Description / Notes">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item label="Receipt File Upload">
          <Upload
            customRequest={handleUploadReceipt}
            maxCount={1}
            showUploadList={false}
            accept="image/*,application/pdf"
          >
            <Button loading={uploading} icon={<UploadOutlined />}>
              {uploading ? "Uploading..." : "Upload Receipt"}
            </Button>
          </Upload>
          {receipt?.name && (
            <span className="text-xs text-slate-500 ml-3">{receipt.name}</span>
          )}
        </Form.Item>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={close}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={createMutation.isLoading}>
            Create
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
